import React, { useState } from 'react';
import {
  IconButton,
  Badge,
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  Divider,
  Button,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  CheckCircle,
  Delete,
  Warning,
  Info,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useNotifications } from '../../contexts/NotificationContext';
import { Notification, NotificationPriority } from '../../types/notification';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

const NotificationBell: React.FC = () => {
  const { 
    notifications, 
    unreadCount, 
    loading, 
    error,
    refreshNotifications,
    markAsRead, 
    markAllAsRead, 
    deleteNotification 
  } = useNotifications();
  
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const navigate = useNavigate();

  // Don't render if not authenticated
  const token = localStorage.getItem('token');
  if (!token) {
    return null;
  }

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    // Fetch fresh notifications when user opens the dropdown
    await refreshNotifications();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notification: Notification) => {
    try {
      if (!notification.read) {
        await markAsRead(notification._id);
      }
      
      if (notification.actionUrl) {
        navigate(notification.actionUrl);
        handleClose();
      }
    } catch (err) {
      console.error('Error handling notification click:', err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllAsRead();
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const handleDelete = async (notificationId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await deleteNotification(notificationId);
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  };

  const getPriorityColor = (priority: NotificationPriority) => {
    switch (priority) {
      case 'critical': return '#dc2626';
      case 'high': return '#f59e0b';
      case 'medium': return '#3b82f6';
      case 'low': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getPriorityIcon = (priority: NotificationPriority) => {
    switch (priority) {
      case 'critical': return <ErrorIcon sx={{ color: '#dc2626', fontSize: 20 }} />;
      case 'high': return <Warning sx={{ color: '#f59e0b', fontSize: 20 }} />;
      case 'medium': return <Info sx={{ color: '#3b82f6', fontSize: 20 }} />;
      case 'low': return <Info sx={{ color: '#6b7280', fontSize: 20 }} />;
      default: return <Info sx={{ color: '#6b7280', fontSize: 20 }} />;
    }
  };

  const open = Boolean(anchorEl);
  const displayNotifications = notifications.slice(0, 10);

  return (
    <>
      <IconButton
        onClick={handleClick}
        sx={{ 
          mr: 2,
          color: 'white',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)'
          }
        }}
      >
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon sx={{ color: 'white' }} />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: { xs: '100vw', sm: 400 },
            maxWidth: { xs: '100vw', sm: 400 },
            maxHeight: { xs: '80vh', sm: 600 },
            mt: { xs: 0, sm: 1 },
            ml: { xs: 0, sm: 'auto' },
            mr: { xs: 0, sm: 'auto' },
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: { xs: 0, sm: 2 },
            boxShadow: '0 8px 32px rgba(102, 126, 234, 0.2)',
            border: '1px solid rgba(255, 255, 255, 0.3)'
          }
        }}
      >
        <Box sx={{ 
          p: { xs: 1.5, sm: 2 }, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>Notifications</Typography>
          {unreadCount > 0 && (
            <Button 
              size="small" 
              onClick={handleMarkAllRead}
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              Mark all read
            </Button>
          )}
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={30} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && notifications.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary" sx={{ fontSize: '0.95rem' }}>
              No notifications
            </Typography>
          </Box>
        )}

        {!loading && !error && displayNotifications.length > 0 && (
          <List sx={{ p: 0, maxHeight: { xs: 'calc(80vh - 120px)', sm: 450 }, overflow: 'auto' }}>
            {displayNotifications.map((notification, index) => (
              <React.Fragment key={notification._id}>
                <ListItem
                  button
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    backgroundColor: notification.read ? 'transparent' : 'rgba(102, 126, 234, 0.08)',
                    borderLeft: `4px solid ${getPriorityColor(notification.priority)}`,
                    '&:hover': {
                      backgroundColor: notification.read ? 'rgba(102, 126, 234, 0.05)' : 'rgba(102, 126, 234, 0.12)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', gap: 1 }}>
                    <Box sx={{ mt: 0.5 }}>
                      {notification.icon ? (
                        <Typography sx={{ fontSize: 24 }}>{notification.icon}</Typography>
                      ) : (
                        getPriorityIcon(notification.priority)
                      )}
                    </Box>
                    
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                        <Typography 
                          variant="subtitle2" 
                          sx={{ 
                            fontWeight: notification.read ? 400 : 600,
                            pr: 1
                          }}
                        >
                          {notification.title}
                        </Typography>
                        {!notification.preventDismissal && (
                          <IconButton
                            size="small"
                            onClick={(e) => handleDelete(notification._id, e)}
                            sx={{ ml: 'auto', flexShrink: 0 }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                      
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          mb: 1,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                      >
                        {notification.message}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                          {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                        </Typography>
                        
                        {notification.requiresAction && !notification.actionResolved && (
                          <Chip 
                            label="Action Required" 
                            size="small" 
                            color="error"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                        
                        {notification.requiresAction && notification.actionResolved && (
                          <Chip 
                            label="Resolved" 
                            size="small" 
                            color="success"
                            icon={<CheckCircle sx={{ fontSize: 14 }} />}
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                      
                      {notification.actionUrl && notification.actionLabel && (
                        <Button 
                          size="small" 
                          variant="contained"
                          sx={{ 
                            mt: 1, 
                            textTransform: 'none',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #5568d3 0%, #6a4293 100%)'
                            }
                          }}
                        >
                          {notification.actionLabel}
                        </Button>
                      )}
                    </Box>
                  </Box>
                </ListItem>
                {index < displayNotifications.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}

        {notifications.length > 10 && (
          <>
            <Divider />
            <Box sx={{ p: 1, textAlign: 'center' }}>
              <Button size="small" onClick={() => { navigate('/notifications'); handleClose(); }}>
                View all notifications
              </Button>
            </Box>
          </>
        )}
      </Popover>
    </>
  );
};

export default NotificationBell;
